import { FC, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import ChildCard from "@src/components/shared-layouts/ChildCard";
import CustomDataTable from "@src/components/shared-ui-components/CustomDataTable";
import { TypographyH5 } from "@src/components/shared-ui-components/CustomTypography";
import { Flex } from "@src/components/shared-ui-components/Flex";
import LocationContext from "@src/context/location/locationContext";

import checkOpenTableContext from "@context/CheckOpenInformationTable/checkOpenTableContext";
import ShowErrorToastContext from "@context/showErrorToast/showErrorToastContext";
import { DEVICE, useWindowSizeChanged } from "@hooks/DeviceSize";
import { CustomSmallCardWrapper } from "../../HomeDashboard/style";
import { getCheckData, openColumns, refinedRowData } from "./helper";
import { CheckWrapper, TableWrapper } from "./style";

interface CheckTableType {
  pagination?: boolean;
}

const OpenCheckTable: FC<CheckTableType> = ({ pagination = false }) => {
  const [rowData, setRowsData] = useState<[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const navigate = useNavigate();
  const filterBy = "open";
  const title = "Open";
  const navigateUrl = "/check-information/open-checks";
  const errorMessage = "Unable to Fetch Open Check";

  const [{ checkOpenTableState, checkOpenTableSetState }] = useContext(
    checkOpenTableContext
  );

  const { identifier, sortingType, page } = checkOpenTableState;

  const [{ locationState }] = useContext(LocationContext);
  const { property, store, revenueCenter, date } = locationState;
  const [{ showErrorToastState, showErrorToastSetState }] = useContext(
    ShowErrorToastContext
  );
  const sortBy = {
    tableName: "",
    label: identifier,
    order: sortingType,
  };

  const sortHandler = (_: string, sortId: string) => {
    checkOpenTableSetState((pre) => {
      return {
        ...pre,
        identifier: sortId,
        sortingType: pre.sortingType === "desc" ? "asc" : "desc",
      };
    });
  };

  const handleNavigate = () => {
    navigate(navigateUrl);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    checkOpenTableSetState((pre) => {
      return { ...pre, page: newPage - 1 };
    });
  };

  const getData = async (customFilter: CustomFilterType) => {
    setIsLoading(true);
    localStorage.setItem(
      "openCheckTableFilter",
      JSON.stringify({
        sortingType: customFilter.sortingType,
        page: customFilter.page,
        identifier: customFilter.identifier,
      })
    );
    try {
      const res = await getCheckData(customFilter);
      if (!res.error) {
        setRowsData(res?.data);
      } else {
        showErrorToastSetState({
          errorMessage: res.error || "",
          errorTitle: errorMessage,
          open: true,
        });
      }
    } catch (err) {
      showErrorToastSetState({
        ...showErrorToastState,
        errorTitle: errorMessage,
        open: true,
      });
    }
    setIsLoading(false);
  };

  useEffect(() => {
    const customFilter: CustomFilterType = {
      property,
      store,
      rvc: revenueCenter,
      date,
      identifier,
      sortingType,
      page: pagination ? page : 0,
      filterBy,
    };
    getData(customFilter);
  }, [property, store, revenueCenter, date, identifier, sortingType, page]);

  const rowDataTable = refinedRowData(rowData);

  const device = useWindowSizeChanged();
  const isDesktop = device === DEVICE.DESKTOP;

  return (
    <CustomSmallCardWrapper>
      <ChildCard title={`${title} Checks`} hideDivider isDataTable>
        <CheckWrapper isDesktop={isDesktop} isNotEmpty={!!rowDataTable?.length}>
          <Flex>
            <strong>Note:&nbsp;</strong>
            <TypographyH5 className="note">
              View check details by selecting check #.
            </TypographyH5>
          </Flex>
          <TableWrapper>
            <CustomDataTable
              sortHandler={sortHandler}
              rowData={rowDataTable}
              page={page}
              showPagination={pagination}
              sortBy={sortBy}
              handleNavigate={handleNavigate}
              handlePageChange={handleChangePage}
              isLoading={isLoading}
              dyColumns={openColumns}
            />
          </TableWrapper>
        </CheckWrapper>
      </ChildCard>
    </CustomSmallCardWrapper>
  );
};

OpenCheckTable.defaultProps = {
  pagination: false,
};

export default OpenCheckTable;
